import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Feather, Loader2, Heart, ArrowRight, BookOpen } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Gallery() {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  const sorted = sortBy === 'hearts'
    ? [...stories].sort((a, b) => b.reactions - a.reactions)
    : stories;

  useEffect(() => {
    axios
      .get(`${API}/public/gallery`)
      .then(({ data }) => setStories(data))
      .catch(() => setError('Could not load the gallery. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50" data-testid="gallery-page">
      <header className="border-b border-amber-200/50 bg-white/50 backdrop-blur-sm">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2" data-testid="gallery-brand-link">
            <div className="bg-amber-100 p-2 rounded-full">
              <Feather className="h-5 w-5 text-amber-800" />
            </div>
            <span className="font-display text-lg font-bold text-amber-950">Flash Fiction</span>
          </Link>
          <Link to="/register">
            <Button size="sm" className="bg-amber-900 hover:bg-amber-800 text-amber-50" data-testid="gallery-signup-cta">
              Write your own
              <ArrowRight className="ml-1 h-3 w-3" />
            </Button>
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-6 py-12 max-w-5xl">
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-amber-950 mb-2" data-testid="gallery-title">
          Story Gallery
        </h1>
        <p className="text-amber-800/70 mb-6">Flash fiction shared by our writers. Tap a story to read and send a heart.</p>

        {stories.length > 1 && (
          <div className="flex items-center space-x-2 mb-8" data-testid="gallery-sort-options">
            {[['newest', 'Newest'], ['hearts', 'Most hearted']].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setSortBy(key)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  sortBy === key
                    ? 'bg-amber-900 text-amber-50'
                    : 'bg-white/70 border border-amber-200 text-amber-800 hover:bg-amber-100'
                }`}
                data-testid={`gallery-sort-${key}`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {loading && (
          <div className="flex justify-center py-20" data-testid="gallery-loading">
            <Loader2 className="h-8 w-8 animate-spin text-amber-800" />
          </div>
        )}

        {error && (
          <p className="text-rose-700 text-center py-10" data-testid="gallery-error">{error}</p>
        )}

        {!loading && !error && stories.length === 0 && (
          <div className="text-center py-20" data-testid="gallery-empty">
            <BookOpen className="h-10 w-10 mx-auto text-amber-400 mb-3" />
            <p className="text-amber-800">No stories have been shared yet. Be the first!</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {sorted.map((story) => (
            <Link
              key={story.share_token}
              to={`/share/${story.share_token}`}
              className="group block bg-white/70 backdrop-blur-sm border border-amber-200 rounded-2xl p-6 hover:shadow-lg hover:border-amber-300 transition-all"
              data-testid={`gallery-card-${story.share_token}`}
            >
              <div className="flex items-center justify-between mb-3">
                {story.category && (
                  <span className="inline-block bg-amber-100 text-amber-800 text-xs uppercase tracking-wider px-3 py-1 rounded-full capitalize">
                    {story.category}
                  </span>
                )}
                <span className="flex items-center text-sm text-rose-500">
                  <Heart className="h-4 w-4 mr-1 fill-rose-400 text-rose-400" />
                  {story.reactions}
                </span>
              </div>
              <h2 className="font-display text-2xl font-bold text-amber-950 mb-1 group-hover:text-amber-800 transition-colors">
                {story.title}
              </h2>
              <p className="text-sm text-amber-800/60 mb-3">
                By {story.author_name} &middot; {story.word_count} words
              </p>
              <p className="font-serif text-gray-700 leading-relaxed line-clamp-3">
                {story.excerpt}&hellip;
              </p>
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
}
